import * as i18n from 'i18next';
import moment from 'moment';
import {
    VideoCallEvent, UserMap, VideoCall, VideoCallOfferEvent, VideoCallRejectEvent,
    VideoCallChatEvent, VideoCallButtonEvent, VideoCallOverrideEvent, Group, Building,
} from 'shared/models';

export class EcsTranscript {
    readonly t: i18n.TFunction;
    readonly videoCall: VideoCall;
    readonly events: VideoCallEvent[];
    readonly buildingName: string | null;
    readonly elevatorName: string;
    readonly userName: string | null;

    constructor(videoCall: VideoCall, events: VideoCallEvent[], building: Building | null, group: Group | null, users: UserMap, t: i18n.TFunction) {
        this.videoCall = videoCall;
        this.events = events;
        this.t = t;

        this.buildingName = building?.displayName || building?.urlSlug || null;
        this.elevatorName = group?.displayName || videoCall.elevatorId;

        const user = users[videoCall.userId!] || null;
        this.userName = user?.displayName || videoCall.userId;
    }

    public toCsv(): string {
        return [
            this.generateCsvHeader(),
            ...this.events.map(e => this.generateCsvLine(e)).filter(line => !!line),
        ].join('\n');
    }
    
    generateCsvHeader(): string {
        return [
            this.t('VIDEO.CSV.ID'),
            this.t('VIDEO.CSV.TIME'),
            this.t('VIDEO.CSV.DETAILS'),
        ].join(',');
    }
    
    generateCsvLine(event: VideoCallEvent): string | null {
        const detailsTxt = this.generateTxtDetails(event);
        if (!detailsTxt) return null;

        const m = moment.utc(event.timestamp);
        return [
            event.id,
            m.format('YYYY/MM/DD HH:mm:ss'),
            this.csvFromTxt(detailsTxt),
        ].join(',');
    }

    csvFromTxt(input: string): string {
        if (input.indexOf(',') >= 0 || input.indexOf('"') >= 0) {
            return `"${input.replace(/"/g, '""')}"`;
        }
        return input;
    }
    
    generateTxtDetails({ type, details }: VideoCallEvent): string | null {
        switch (type) {
            case 'CALL': return this.generateCallTxtDetails();
            case 'MSCL': return this.generateMSCallTxtDetails();
            case 'LPCL': return this.generateLPCallTxtDetails();
            case 'CLID': return this.generateCallIdTxtDetails();
            case 'OFFR': return this.generateOfferTxtDetails(details as VideoCallOfferEvent['details']);
            case 'RJCT': return this.generateRejectTxtDetails(details as VideoCallRejectEvent['details']);
            case 'CHAT': return this.generateChatTxtDetails(details as VideoCallChatEvent['details']);
            case 'BTTN': return this.generateButtonTxtDetails(details as VideoCallButtonEvent['details']);
            case 'HLPA': return this.generateHelpArrivedTxtDetails();
            case 'HLPC': return this.generateHelpComingTxtDetails();
            case 'OVRD': return this.generateOverrideTxtDetails(details as VideoCallOverrideEvent['details']);
            case 'GBYE': return this.generateGoodbyeTxtDetails();
            default: return null;
        }
    }

    populateTemplate(templateKey: string, params: { [tag: string]: string } = {}): string {
        const template = this.t(templateKey);

        return template.replace(/([^<]*)(?:<([^>]+)\/>)?/g, (match, text: string, tag: string) => {
            let replacement = '';
            if (tag && params[tag]) {
                replacement = params[tag];
            }
            else if (tag === 'ELEVATOR') {
                replacement = this.elevatorName;
            }
            else if (tag === 'BUILDING') {
                replacement = this.buildingName!;
            }
            else if (tag === 'USER') {
                replacement = this.userName || '?';
            }
            else if (tag === 'CALL') {
                replacement = this.videoCall.callId;
            }
            return `${text}${replacement}`;
        });
    }

    generateCallTxtDetails(): string {
        if (!this.buildingName) {
            return this.populateTemplate(`VIDEO.CALL_FROM_EL`);
        }

        return this.populateTemplate(`VIDEO.EVENT.CALL_DETAIL`);
    }

    generateMSCallTxtDetails(): string {
        return this.populateTemplate(this.videoCall.source === 'MS' ? 'VIDEO.EVENT.MSCL_NEW_DETAIL' : 'VIDEO.EVENT.MSCL_DETAIL');
    }

    generateLPCallTxtDetails(): string {
        return this.populateTemplate(this.videoCall.source === 'LP' ? 'VIDEO.EVENT.LPCL_NEW_DETAIL' : 'VIDEO.EVENT.LPCL_DETAIL');
    }

    generateCallIdTxtDetails(): string {
        return this.populateTemplate('VIDEO.EVENT.CLID_DETAIL');
    }

    generateOfferTxtDetails({ seq }: VideoCallOfferEvent['details']): string | null {
        if (seq > 0) return null;
        return this.populateTemplate('VIDEO.EVENT.OFFR_0_DETAIL');
    }

    generateRejectTxtDetails({ reason }: VideoCallRejectEvent['details']): string | null {
        return this.populateTemplate(`VIDEO.EVENT.RJCT_${reason}_DETAIL`);
    }

    generateChatTxtDetails({ text }: VideoCallChatEvent['details']): string | null {
        return (
            text === null
                ? this.populateTemplate('VIDEO.EVENT.CHAT_DETAIL_NULL')
                : this.populateTemplate('VIDEO.EVENT.CHAT_DETAIL', { TEXT: text })
        );
    }

    generateButtonTxtDetails({ yes, no, help }: VideoCallButtonEvent['details']): string | null {
        if (typeof(yes) !== 'boolean' && typeof(no) !== 'boolean' && typeof(help) !== 'boolean') {
            return this.populateTemplate('VIDEO.EVENT.BTTN_DETAIL_NULL');
        }

        if (!yes && !no && !help) return null;
        return this.populateTemplate('VIDEO.EVENT.BTTN_DETAIL', { BUTTON: this.t(yes ? 'VIDEO.BUTTON.YES' : no ? 'VIDEO.BUTTON.NO' : help ? 'VIDEO.BUTTON.HELP' : '?') });
    }

    generateHelpArrivedTxtDetails(): string {
        return this.populateTemplate('VIDEO.EVENT.HLPX_DETAIL', { TEXT: this.t('VIDEO.EVENT.HLPA') });
    }

    generateHelpComingTxtDetails(): string {
        return this.populateTemplate('VIDEO.EVENT.HLPX_DETAIL', { TEXT: this.t('VIDEO.EVENT.HLPC') });
    }

    generateOverrideTxtDetails({ callId }: VideoCallOverrideEvent['details']): string {
        return this.populateTemplate('VIDEO.EVENT.OVRD_DETAIL', { CALL: callId });
    }

    generateGoodbyeTxtDetails(): string {
        return this.populateTemplate('VIDEO.EVENT.GBYE_DETAIL');
    }
}
